<template>
  <Fieldset :legend="$t('modal.apiKeys.localStorage')">
    <div class="grid mt-2 grid-cols-1 gap-3">
      <div>
        <IftaLabel>
          <Password fluid v-model="jupApiKey" :feedback="false" toggle-mask>
          </Password>
          <label>{{ $t('modal.apiKeys.apiKey') }}</label>
        </IftaLabel>
      </div>
      <div>
        <IftaLabel>
          <Password fluid v-model="jupPrivateKey" :feedback="false" toggle-mask>
          </Password>
          <label>{{ $t('modal.apiKeys.privateKeySol') }}</label>
        </IftaLabel>
      </div>
      <Button @click="save" severity="secondary" icon="pi pi-save" :label="$t('save')" fluid />
    </div>
  </Fieldset>
</template>

<script lang="ts" setup>
import Password from 'primevue/password';
import IftaLabel from 'primevue/iftalabel';
import Button from 'primevue/button';
import Fieldset from 'primevue/fieldset';
import { computed, onMounted, ref } from 'vue';
import { useMyToast } from '@/composable/useMyToast';
import { i18n } from '@/i18n';
import { useLocalKeys } from "@/composable/useLocalKeys"
import { Keypair } from '@solana/web3.js'
import bs58 from 'bs58'
import { JupiterService } from '@/services/jupiter-service'

const { t } = i18n.global
const { addToast } = useMyToast()

const jupPrivateKey = ref<string>()
const jupApiKey = ref<string>()

const oneInchKeys = useLocalKeys()

const cacheKeyJupPrivate = computed(() => `jup-xprv`)
const cacheKeyJupApi = computed(() => `jup-api`)

const save = async () => {
  // Validate API key format
  if (!jupApiKey.value || jupApiKey.value.trim().length < 10) {
    addToast({
      severity: 'error',
      message: t('modal.apiKeys.errors.invalidApiKeyFormat')
    })
    return
  }

  // Validate private key format if provided
  if (jupPrivateKey.value) {
    try {
      const decoded = bs58.decode(jupPrivateKey.value)
      Keypair.fromSecretKey(decoded)
    } catch {
      addToast({
        severity: 'error',
        message: t('modal.apiKeys.errors.invalidPrivateKeyFormat')
      })
      return
    }
  }

  // Verify API key via real request
  try {
    const res = await JupiterService.search(jupApiKey.value, 'SOL')
    if (res.data && 'error' in res.data) {
      throw new Error('API key verification failed')
    }
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    addToast({
      severity: 'error',
      message: `${t('modal.apiKeys.errors.apiKeyVerificationFailed')}: ${msg}`
    })
    return
  }

  oneInchKeys.save(cacheKeyJupApi.value, jupApiKey.value)
  oneInchKeys.save(cacheKeyJupPrivate.value, jupPrivateKey.value)
  addToast({
    severity: "success",
    message: t("changesSaved")
  })
}

onMounted(() => {
  jupApiKey.value = oneInchKeys.get(cacheKeyJupApi.value)
  jupPrivateKey.value = oneInchKeys.get(cacheKeyJupPrivate.value)
})
</script>
